<template>
  <v-card flat :class="{ 'dialog-card': $vuetify.breakpoint.smAndDown }">
    <v-toolbar flat light color="white" v-if="$vuetify.breakpoint.smAndDown">
      <v-toolbar-title>{{ $t('cpd-text-1') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click.native="closeDialog">
        <v-icon>fa-times</v-icon>
      </v-btn>
    </v-toolbar>
    <v-divider></v-divider>
    <v-container fluid class="grow">
      <v-row>
        <v-col cols="12" class="text-right" v-if="!$vuetify.breakpoint.smAndDown">
          <v-btn color="color-grey" depressed right text small @click="closeDialog">
            <v-icon class="mr-3">fa-times</v-icon>
            <span>{{ $t('close') }}</span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" v-if="$vuetify.breakpoint.mdAndUp">
          <h2 class="my-0 text-h2 text-center">{{ $t('cpd-text-1') }}</h2>
        </v-col>
      </v-row>
      <v-row justify="center" v-if="debt !== null">
        <div class="pa-3">
          <span :class="{'ok-title big mr-3': $vuetify.breakpoint.mdAndUp, 'ok-title small': $vuetify.breakpoint.smAndDown }">{{ $t('debt') }}</span>
          <br v-if="$vuetify.breakpoint.smAndDown" />
          <span class="ok-subtitle small" data-matomo-mask>{{debt.orderNr}}</span>
        </div>
        <div class="pa-3">
          <span :class="{'ok-title big mr-3': $vuetify.breakpoint.mdAndUp, 'ok-title small': $vuetify.breakpoint.smAndDown }">{{ $t('creditor') }}</span>
          <br v-if="$vuetify.breakpoint.smAndDown" />
          <span class="ok-subtitle small">{{debt.creditor.name}}</span>
        </div>
      </v-row>
      <v-row justify="center">
        <v-col cols="10" md="12" :class="{ 'text-center': $vuetify.breakpoint.mdAndUp }">
          <p class="mb-3">{{ $t('cpd-text-2') }}</p>
          <p v-if="paymentPlan">{{ $t('cpd-text-3') }}</p>
          <v-alert type="warning" v-if="summary.state.state === 4">
            {{ $t('inCourtPaymentWarning') }}
          </v-alert>
        </v-col>
      </v-row>
      <v-row no-gutters align="center" justify="center" v-if="paymentData === null">
        <v-col cols="10" md="12" :class="{ 'text-center': $vuetify.breakpoint.mdAndUp }">
          <p class="ok-title mb-2">{{ $t('cpd-text-4') }} (5 - {{summary.left | currency}})</p>
        </v-col>
        <v-col cols="10" md="2">
          <v-text-field v-model="amount"
                        type="number"
                        min="5"
                        :max="summary.left"
                        step="any"
                        append-icon="fas fa-euro-sign"
                        @change="$v.amount.$touch()"
                        @blur="$v.amount.$touch()"
                        :error-messages="amountErrors"
                        outlined
                        class="text-center"></v-text-field>
        </v-col>
      </v-row>
      <v-row no-gutters v-else>
        <v-col cols="12" class="text-center mb-6">
          <span class="ok-title big mr-3">{{ $t('amountToPay') }}</span>
          <span class="ok-subtitle color-new-link-blue--text">{{ amount | currency }}</span>
        </v-col>
      </v-row>
      <v-row no-gutters v-if="$vuetify.breakpoint.mdAndUp">
        <v-col cols="12">
          <shared-payment-forms :paymentData="paymentData" @cancelled="cancelled"></shared-payment-forms>
        </v-col>
      </v-row>
      <v-dialog v-model="mobilePaymentDialog"
                fullscreen
                hide-overlay
                transition="dialog-bottom-transition"
                content-class="payment-dialog"
                scrollable
                v-else>
        <shared-payment-forms :paymentData="paymentData" @cancelled="cancelled"></shared-payment-forms>
      </v-dialog>
    </v-container>
    <v-card-actions class="justify-center pb-6" v-if="!hidePaymentButtons">
      <div class="pa-3" v-if="$vuetify.breakpoint.mdAndUp">
        <v-btn tile outlined color="color-new-link-blue" @click="closeDialog">{{ $t('cancel') }}</v-btn>
      </div>
      <div class="pa-3">
        <v-btn color="primary" tile @click="setPaymentData" :disabled="$v.$invalid || hidePaymentButtons" class="payment-button payment-button-custom">{{ $t('continueToPay') }}</v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { required, between } from 'vuelidate/lib/validators'

  export default {
    props: ['debt', 'paymentPlan'],
    data: function () {
      return {
        paymentData: null,
        amount: 5.00,
        hidePaymentButtons: false,
        mobilePaymentDialog: false
      }
    },
    methods: {
      closeDialog () {
        this.paymentData = null
        this.hidePaymentButtons = false
        this.mobilePaymentDialog = false
        this.$emit('close')
      },
      setPaymentData () {
        if (!this.$v.$invalid) {
          if (this.$vuetify.breakpoint.smAndDown) {
            this.mobilePaymentDialog = true
          } else {
            this.mobilePaymentDialog = false
          }

          this.paymentData = {
            debtNr: this.debtNr,
            description: null,
            amount: this.amount,
            msnr: null,
            paymentType: 'custom'
          }

          this.hidePaymentButtons = true
          this.$matomo && this.$matomo.trackEvent("Payment", "Payment", "Payment Custom Start")
        }
      },
      addBtnClick (value) {
        var amount = this.financial(this.amount) + value
        if (amount >= 5 && amount <= this.summary.left) {
          this.amount = amount
        } else {
          this.amount = amount <= 5 ? 5 : this.summary.left
        }
      },
      financial (x) {
        if (isNaN(x)) {
          x = x.toFixed(2)
        }
        return Number.parseFloat(x)
      },
      cancelled () {
        this.hidePaymentButtons = false
        this.mobilePaymentDialog = false
        this.paymentData = null
      }
    },
    watch: {
      amount: function (val) {
        this.paymentData = null
      }
    },
    computed: {
      debtNr () {
        if (this.debt !== null) {
          return this.debt.nr
        } else if (this.paymentPlan !== null) {
          return this.paymentPlan.debts[0].nr
        }
      },
      summary () {
        if (this.debt !== null) {
          return this.debt.debtSummary
        } else if (this.paymentPlan !== null) {
          return this.paymentPlan.summary
        }
      },
      amountErrors () {
        const errors = []
        if (!this.$v.amount.$dirty) return errors
        !this.$v.amount.between && errors.push(this.$t('validation.amount.between') + ' 5 € - ' + Math.floor(this.summary.left) + ' €')
        !this.$v.amount.required && errors.push(this.$t('validation.amount.required'))
        return errors
      }
    },
    validations: {
      amount: {
        required,
        between (value) {
          return between(5.00, this.summary.left)(value)
        }
      }
    }
  }
</script>

<i18n>
  {
  "fi": {
  "cpd-text-1": "Maksa vapaavalintainen summa",
  "cpd-text-2": "Voit lyhentää velkaasi vapaavalintaisella summalla (vähintään 5€)",
  "cpd-text-3": "Lyhennyksesi otetaan huomioon tulevissa maksuerissä.",
  "cpd-text-4": "Syötä summa"
  },
  "se": {
  "cpd-text-1": "Betala en valfri summa",
  "cpd-text-2": "Du kan korta av din skuld med ett valfritt belopp (minst 5€)",
  "cpd-text-3": "Din betalning beaktas i de kommande betalningsraterna.",
  "cpd-text-4": "Fyll i summan"
  },
  "en": {
  "cpd-text-1": "Pay a voluntary amount",
  "cpd-text-2": "You can pay you debt with an amount that is best suited for you (minimum 5€)",
  "cpd-text-3": "Your payment will be taken into account in the upcoming installments.",
  "cpd-text-4": "Insert amount"
  }
  }
</i18n>
